import { AsyncPipe } from "@angular/common";
import { Component, OnInit, ViewEncapsulation } from "@angular/core";
import { ActivatedRouteSnapshot, Router, RouterOutlet } from "@angular/router";
import { AccessLevel, AccountMarketplace, AccountType, Currency, User } from "@front/m19-api-client";
import {
  AccountSelectionService,
  AuthService,
  CurrencyService,
  Hotkeys,
  NotificationService,
  OrganizationAccountGroupService,
  UserSelectionService,
} from "@front/m19-services";
import { IButtonComponent, IToasterComponent, ModalOptions, ModalService } from "@front/m19-ui";
import { ObfuscationService } from "@m19-board/services/obfuscation.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { combineLatest, Observable } from "rxjs";
import { delay, filter, switchMap } from "rxjs/operators";
import { environment } from "@m19-board/environment";
import { DebugInfoModalComponent } from "./debug-info-modal.component";
import { BoardSidebarComponent } from "./board-sidebar/board-sidebar.component";
import { PageLayoutComponent } from "@m19-board/containers/page-layout/page-layout.component";

@UntilDestroy()
@Component({
  templateUrl: "./board-layout.component.html",
  encapsulation: ViewEncapsulation.None,
  imports: [AsyncPipe, IButtonComponent, BoardSidebarComponent, IToasterComponent, PageLayoutComponent, RouterOutlet],
  standalone: true,
})
export class BoardLayoutComponent implements OnInit {
  isTest = !environment.production;

  user!: User;
  currency!: Currency;
  locale!: string;
  betaTest = false;
  invalidAccountMarketplaces: AccountMarketplace[] = [];
  pendingValidationAccountMarketplaces: AccountMarketplace[] = [];

  globalWarning$: Observable<string>;
  allAccountMarketplaces: AccountMarketplace[] = [];
  accountMarketplaces: AccountMarketplace[] | undefined;
  selectedDateRage!: string[];

  constructor(
    private authService: AuthService,
    private accountGroupService: OrganizationAccountGroupService,
    private userSelectionService: UserSelectionService,
    public currencyService: CurrencyService,
    private router: Router,
    private notificationService: NotificationService,
    private accountSelectionService: AccountSelectionService,
    private modalService: ModalService,
    private hotkeys: Hotkeys,
    private obfuscationService: ObfuscationService,
  ) {
    this.globalWarning$ = this.notificationService.globalWarning$;
    this.hotkeys
      .addShortcut({ altKey: "KeyD" })
      .pipe(untilDestroyed(this))
      .subscribe(() => this.printDebugInfo());
    this.hotkeys
      .addShortcut({ altKey: "KeyO" })
      .pipe(untilDestroyed(this))
      .subscribe(() => this.obfuscationService.toggleObfuscationMode(!this.obfuscationService.obfuscation.value));

    this.userSelectionService.selectedCurrency$.pipe(untilDestroyed(this)).subscribe((x) => (this.currency = x));

    this.authService.loggedUser$.pipe(untilDestroyed(this)).subscribe((user) => {
      this.user = user;
      this.locale = user.locale;
      this.betaTest = user.betaTester ?? false;
    });
    this.userSelectionService.dateRange$.pipe(untilDestroyed(this)).subscribe((dr) => {
      this.selectedDateRage = dr;
    });
  }

  ngOnInit(): void {
    // init account selector based on router state
    let routerState: ActivatedRouteSnapshot[] = [this.router.routerState.snapshot.root];
    while (routerState.length > 0) {
      const state = routerState.pop();
      if (state && state.children) {
        routerState = [...routerState, ...state.children];
      }
    }

    this.accountGroupService.allOrganizationAccountGroups$
      .pipe(
        untilDestroyed(this),
        filter((organizations) => organizations != undefined),
      )
      .subscribe((organizations) => {
        this.allAccountMarketplaces =
          organizations?.flatMap((o) => o.accountGroups.flatMap((ag) => ag.resources)) ?? [];
      });

    this.accountGroupService.allOrganizationAccountGroups$
      .pipe(
        untilDestroyed(this),
        filter((organizations) => organizations != undefined),
        switchMap(() =>
          combineLatest([
            this.notificationService.getNotifications$.pipe(filter((n) => n != undefined)),
            this.accountSelectionService.accountGroupSelection$.pipe(filter((a) => a != undefined)),
            this.accountSelectionService.accountMarketplacesSelection$,
          ]).pipe(
            delay(10), // delay subscription so as to occur after view check
          ),
        ),
      )
      .subscribe(([notifications, accountGroup, accountMarketplaces]) => {
        this.invalidAccountMarketplaces = accountGroup
          .getAccountMarketplaces()
          .filter((am) => am.accessLevel != AccessLevel.READ && !am.isValidToken);
        this.pendingValidationAccountMarketplaces = accountGroup
          .getAccountMarketplaces()
          .filter(
            (am) =>
              am.accessLevel != AccessLevel.READ &&
              am.accountType == AccountType.VENDOR &&
              am.isValidToken &&
              !am.approvedBy &&
              (am.nbAsinsToValidate ?? 0) > 0,
          );
        this.accountMarketplaces = accountMarketplaces;
      });
  }

  isAdminLogin(): boolean {
    return this.authService.isAdminLogin();
  }

  printDebugInfo() {
    const modalOptions: ModalOptions<null> = {
      modalTitle: "Debug Information",
      modalTitleIcon: "icon-[lucide--bug]",
      closeable: true,
      fullScreen: false,
      adaptiveWidth: false,
      position: "center",
    };
    this.modalService.openModal(DebugInfoModalComponent, modalOptions);
  }
}
